import { useMemo } from 'react';
import type { Product } from '../../../../data/product';
import { compare, multiSort } from '../../../../helpers/column-sort';
import { type Family, UNASSIGNED_FAMILY_ID } from '../data/family';
import type { ProductOrderItem } from '../data/product-order-item';
import { familyTotals } from '../utils/family-aggregates';
import { stripFamilyPrefix } from '../utils/family-id';
import { familyExpectedSavings90d } from '../utils/family-priority';
import { familyDisplay } from '../utils/family-title';

export function useFamilies(orderItems: ProductOrderItem[]): Family[] {
  return useMemo(() => resolveFamilies(orderItems), [orderItems]);
}

/**
 * Groups order items by prefix-stripped family_id. Exported for unit testing.
 * Members are ordered by size_ounces descending (largest format first), with
 * item_code as a deterministic tiebreaker.
 */
export function resolveFamilies(orderItems: ProductOrderItem[]): Family[] {
  if (orderItems.length === 0) {
    return [];
  }

  const groups = new Map<string, ProductOrderItem[]>();
  const allProducts: Product[] = [];
  const productsByBrand = new Map<string, Product[]>();
  for (const item of orderItems) {
    const key = groupKey(item.product.family_id);
    const bucket = groups.get(key);

    if (bucket) {
      bucket.push(item);
    } else {
      groups.set(key, [item]);
    }

    allProducts.push(item.product);
    const brandBucket = productsByBrand.get(item.product.brand);
    if (brandBucket) {
      brandBucket.push(item.product);
    } else {
      productsByBrand.set(item.product.brand, [item.product]);
    }
  }

  const families: Family[] = [];
  for (const [familyId, members] of groups) {
    members.sort(byLargestSizeFirst);
    const { brand, name } = familyDisplay(members);

    // Brand-narrow the candidate pool when we have a brand; otherwise consider
    // every product. Then drop products already in this family.
    const pool = brand ? (productsByBrand.get(brand) ?? []) : allProducts;
    const candidates = pool.filter(
      (p) => p.family_id === '' || stripFamilyPrefix(p.family_id) !== familyId
    );

    const totals = familyTotals(members);
    const family: Family = {
      id: familyId,
      brand,
      name,
      members,
      candidates,
      totals,
      expectedSavings90d: 0,
    };
    family.expectedSavings90d = familyExpectedSavings90d(family);
    families.push(family);
  }

  return families;
}

const byLargestSizeFirst = multiSort<ProductOrderItem>(
  (a, b) => compare(b.product.size_ounces ?? 0, a.product.size_ounces ?? 0),
  (a, b) => compare(a.product.item_code, b.product.item_code)
);

function groupKey(familyID: string): string {
  if (!familyID) {
    return UNASSIGNED_FAMILY_ID;
  }

  return stripFamilyPrefix(familyID);
}
